import { gql } from "@apollo/client";
import {
  Box,
  createStyles,
  FormControlLabel,
  FormGroup,
  makeStyles,
  Switch,
  Typography,
} from "@material-ui/core";
import { useLogEvent } from "analytics";
import { useAuthContext } from "auth/context";
import {
  OrganizationTheme,
  UpdateCompanySettingsShowTrim,
  UpdateCompanySettingsShowTrimVariables,
  UpdateCompanySettingsTheme,
  UpdateCompanySettingsThemeVariables,
} from "generated/schemaTypes";
import { useState } from "react";
import { ResourceCustomTheme } from "styles/config";
import { PermissionEnum } from "utils/permissions";
import useDebouncedMutation from "utils/useDebouncedMutation";
import useMutation from "utils/useMutation";

import ColorPicker from "./ColorPicker";

export type CompanyTheme = {
  primaryColor: string;
  secondaryColor?: string;
};

export const ORGANIZATION_THEME_FRAGMENT = gql`
  fragment OrganizationTheme on Theme {
    primaryColor
    secondaryColor
  }
`;

const UPDATE_THEME = gql`
  mutation UpdateCompanySettingsTheme($input: UpdateOrganizationThemeInput!) {
    updateOrganizationTheme(input: $input) {
      success
      code
      message
      organization {
        id
        theme {
          ...OrganizationTheme
        }
      }
    }
  }
  ${ORGANIZATION_THEME_FRAGMENT}
`;

const UPDATE_TRIM = gql`
  mutation UpdateCompanySettingsShowTrim(
    $input: UpdateOrganizationShowTrimInput!
  ) {
    updateOrganizationShowTrim(input: $input) {
      success
      code
      message
      organization {
        id
        showTrim
      }
    }
  }
`;

const useStyles = makeStyles<ResourceCustomTheme>(
  (theme: ResourceCustomTheme) =>
    createStyles({
      switchLabel: {
        margin: "0.8rem 0",
      },
      switch: {
        marginRight: theme.spacing(1),
      },
    })
);

type BrandProps = {
  showTrim: boolean;
  theme: OrganizationTheme | null;
};

function Brand({ showTrim, theme }: BrandProps) {
  const { checkRolePermissions } = useAuthContext();
  const logEvent = useLogEvent({
    component: "Brand",
    project: "Company branding",
  });
  const [primaryColor, setPrimaryColor] = useState<string>(
    theme?.primaryColor?.slice(1) ?? "FFF"
  );
  const classes = useStyles({
    primaryColor,
  });

  const [updateThemeDebounced] = useDebouncedMutation<
    UpdateCompanySettingsTheme,
    UpdateCompanySettingsThemeVariables
  >(UPDATE_THEME, {}, 1000);
  const [saveShowTrim] = useMutation<
    UpdateCompanySettingsShowTrim,
    UpdateCompanySettingsShowTrimVariables
  >(UPDATE_TRIM);

  const handleColorChange = (colorHex: string): void => {
    logEvent("Primary color changed");
    setPrimaryColor(colorHex);
    updateThemeDebounced({
      variables: {
        input: {
          primaryColor: `#${colorHex}`,
        },
      },
    });
  };

  const isAuthorized = checkRolePermissions(
    PermissionEnum.ORGANIZATION_BRAND_WRITE
  );

  return (
    <Box>
      <Typography variant="h3">
        <span role="img" aria-label="Paint Pallette">
          🎨
        </span>{" "}
        Brand Color
      </Typography>
      {isAuthorized && (
        <FormGroup row>
          <FormControlLabel
            className={classes.switchLabel}
            control={
              <Switch
                className={classes.switch}
                checked={showTrim}
                name="trim"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  logEvent("Show trim changed");
                  saveShowTrim({
                    variables: {
                      input: {
                        showTrim: e.target.checked,
                      },
                    },
                  });
                }}
              />
            }
            label="Use as Background Color for Guides"
          />
        </FormGroup>
      )}
      <ColorPicker
        colorHex={primaryColor}
        setColorHex={handleColorChange}
        disabled={!isAuthorized}
      />
    </Box>
  );
}

Brand.fragments = {
  theme: ORGANIZATION_THEME_FRAGMENT,
};

export default Brand;
